import { PartFinderReport } from '@/api/generatedApi';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { useListPartFinderReports } from '@/hooks/api/part-finder-reports/useListPartFinderReports';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { SectionHeader } from '../AssetsPage/SectionHeader';
import { BodyText } from '../BodyText';
import { partFinderReportColumns } from './columnDefinitions';
import { BulkDeleteSidebar } from './BulkDeleteSidebar';

const TableContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
});

const DataGridWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  minWidth: 0,
  height: '100%',
  '& .MuiDataGrid-root': {
    borderRadius: '6px !important',
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cellCheckbox': {
    cursor: 'default',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none !important',
  },
});

export const PartFinderList = () => {
  const gridApi = useGridApiRef();
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [hasReports, setHasReports] = useState<boolean>();

  const { fetchData } = useListPartFinderReports();

  const fetchReports = async ({ page, perPage }: { page: number; perPage: number }) => {
    const response = await fetchData({ page, perPage });
    if (page === 1) {
      setHasReports(response.pageData.total > 0);
    }
    return {
      reports: response.partFinderReports,
      pageData: response.pageData,
    };
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    if (newSelection.type === 'exclude') {
      const allRowIds = gridApi.current?.getAllRowIds() || [];
      const excludedIds = new Set(newSelection.ids);
      const selectedIds = allRowIds.filter(id => !excludedIds.has(id));
      setSelectedReportIds(selectedIds as string[]);
    } else {
      setSelectedReportIds([...newSelection.ids] as string[]);
    }
  };

  const handleDeleteComplete = () => {
    setSelectedReportIds([]);
    gridApi.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
  };

  if (hasReports === false) {
    return;
  }

  return (
    <TableContainer>
      <SectionHeader>
        <BodyText>History</BodyText>
      </SectionHeader>
      <DataGridWrapper>
        <DataGridLazy<PartFinderReport, { reports: PartFinderReport[] }>
          apiRef={gridApi}
          columns={partFinderReportColumns}
          initialState={{ pinnedColumns: { right: ['__check__'] } }}
          fetchRows={fetchReports}
          dataKey='reports'
          rowHeight={60}
          columnHeaderHeight={40}
          checkboxSelection
          onRowSelectionModelChange={handleSelectionChange}
          density='compact'
          disableRowSelectionOnClick
          disableColumnSorting
          hideFooter={false}
          slotProps={{
            footer: {
              sx: {
                flexDirection: 'row-reverse',
                minHeight: '27px',
                padding: '0 0 0 10px',
              },
            },
            footerRowCount: { sx: { margin: 0 } },
          }}
          localeText={{
            footerTotalRows: 'Reports:',
            footerRowSelected(count) {
              return `Selected: ${count} `;
            },
          }}
        />
        <BulkDeleteSidebar selectedReportIds={selectedReportIds} onDelete={handleDeleteComplete} />
      </DataGridWrapper>
    </TableContainer>
  );
};
